import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseCalculPrixAchat, normalizeVenteAchatRow } from './parametre-doc-api-normalize';

export const DEBUG_PARAMETRE_DOCUMENT_API = true;

export interface ParametreDocumentStationSettings {
    id?: number;
    station_id: number;
    modele: 1 | 2;
    langue: 'francais' | 'anglais' | 'arabe';
    active_bon_liv: boolean | 0 | 1;
    /** Mettre à jour le prix d'achat sur la fiche produit à partir du document d'achat. */
    maj_prix_achat_produit?: boolean | 0 | 1;
    /** Prix unitaire d'entrée pour le CMUP : fiche produit ou ligne document. */
    base_calcul_prix_achat?: BaseCalculPrixAchat | string;
    image_cachet: string | null;
    modele_reference: number | null;
    /** Sélection active par type_document_id : { "7": modelId, ... } */
    modeles_reference?: Record<string, number> | string | null;
    document_category?: 'vente' | 'achat' | null;
}

export interface VenteAchatParametreRow {
    id?: number;
    station_id?: number;
    type_document_id: number;
    document_category: 'vente' | 'achat';
    aff_montant_totaux: 0 | 1;
    aff_inf_contact: 0 | 1;
    aff_courte_desp: 0 | 1;
    aff_num_bn_liv: 0 | 1;
    note: string | null;
    label: string | null;
    couleur: string | null;
    date_paiement: 'jours' | 'mois' | 'annee' | null;
    nb_date: number | null;
    insr_orig_artcl: 0 | 1;
    insr_qr_cd: 0 | 1;
    aff_inf?: 0 | 1;
}

export interface ParametreDocumentPutBody {
    active_bon_liv: boolean;
    modele_reference: number | null;
    modeles_reference?: Record<string, number> | null;
    maj_prix_achat_produit?: boolean;
    base_calcul_prix_achat?: BaseCalculPrixAchat;
    vente_achat?: VenteAchatParametreRow[];
}

export interface ParametreDocumentBundle {
    parametre_document: Partial<ParametreDocumentStationSettings>;
    vente_achat: VenteAchatParametreRow[];

    image_cachet?: string | null;
}

export type ParametreDocumentSettings = ParametreDocumentStationSettings & Partial<VenteAchatParametreRow>;

@Injectable({
    providedIn: 'root'
})
export class ParametreDocumentSettingsService {
    private readonly baseUrl = `${environment.parameters_url.replace(/\/$/, '')}/api/settings/parametre-document`;

    constructor(private http: HttpClient) {}

    private debugRequest(method: string, url: string, body?: unknown): void {
        if (!DEBUG_PARAMETRE_DOCUMENT_API) {
            return;
        }
        const payload =
            body === undefined
                ? '(pas de body)'
                : JSON.parse(JSON.stringify(body));
    }

    getByStation(stationId: number): Observable<ParametreDocumentBundle | null> {
        const url = `${this.baseUrl}/${stationId}`;
        this.debugRequest('GET', url);
        return this.http.get<unknown>(url).pipe(
            tap((res) => {

            }),
            map((res) => this.extractBundle(res)),
            catchError((err) => {
                if (err?.status === 404) {
                    return of(null);
                }
                return throwError(() => err);
            })
        );
    }

    getVenteAchatByStationAndType(
        stationId: number,
        typeDocumentId: number
    ): Observable<VenteAchatParametreRow | null> {
        const url = `${this.baseUrl}/${stationId}/vente-achat/${typeDocumentId}`;
        this.debugRequest('GET', url);
        return this.http.get<unknown>(url).pipe(
            tap((res) => {

            }),
            map((res) => this.extractVenteAchatRow(res)),
            catchError(() => of(null))
        );
    }

    update(stationId: number, body: ParametreDocumentPutBody): Observable<unknown> {
        const url = `${this.baseUrl}/${stationId}`;
        this.debugRequest('PUT', url, body);
        return this.http.put(url, body).pipe(
            tap({
                next: (res) => {

                },
                error: (err) => {

                }
            })
        );
    }

    /** Création initiale des paramètres document pour une station (nouvelle station). */
    create(stationId: number, body: ParametreDocumentPutBody): Observable<unknown> {
        const url = `${this.baseUrl}/${stationId}`;
        this.debugRequest('POST', url, body);
        return this.http.post(url, body).pipe(
            tap({
                next: (res) => {},
                error: (err) => {}
            })
        );
    }

    private extractBundle(res: unknown): ParametreDocumentBundle | null {
        if (res == null) {
            return null;
        }
        const root =
            typeof res === 'object' && res !== null && 'data' in res ? (res as { data: unknown }).data : res;
        if (root == null || typeof root !== 'object') {
            return null;
        }
        const raw = root as Record<string, unknown>;

        const venteRaw = raw['vente_achat'];
        const venteAchat: VenteAchatParametreRow[] = Array.isArray(venteRaw)
            ? (venteRaw as VenteAchatParametreRow[])
            : [];

        let parametre_document: Partial<ParametreDocumentStationSettings>;
        const nested = raw['parametre_document'];
        if (nested != null && typeof nested === 'object') {
            parametre_document = nested as Partial<ParametreDocumentStationSettings>;
        } else {
            const { vente_achat: _va, parametre_document: _pd, ...rest } = raw;
            parametre_document = rest as Partial<ParametreDocumentStationSettings>;
        }

        if (
            Object.keys(parametre_document).length === 0 &&
            venteAchat.length === 0 &&
            !('station_id' in parametre_document)
        ) {
            return null;
        }

        const mergedPd: Partial<ParametreDocumentStationSettings> = {
            ...parametre_document,
            modele_reference:
                parametre_document.modele_reference ??
                (this.pickModeleReferenceFromRecord(raw) as number | null | undefined) ??
                (this.pickModeleReferenceFromRecord(
                    parametre_document as Record<string, unknown>
                ) as number | null | undefined) ??
                null,
            modeles_reference:
                (parametre_document.modeles_reference as Record<string, number> | string | null | undefined) ??
                (raw['modeles_reference'] as Record<string, number> | string | null | undefined) ??
                null,
            image_cachet:
                parametre_document.image_cachet ??
                (typeof raw['image_cachet'] === 'string' ? (raw['image_cachet'] as string) : null) ??
                null
        };

        return {
            parametre_document: mergedPd,
            vente_achat: venteAchat
        };
    }

    private pickModeleReferenceFromRecord(rec: Record<string, unknown>): unknown {
        return (
            rec['modele_reference'] ??
            rec['modele_reference_id'] ??
            rec['reference_model_id'] ??
            rec['id_modele_reference']
        );
    }

    private extractVenteAchatRow(res: unknown): VenteAchatParametreRow | null {
        if (res == null || typeof res !== 'object') {
            return null;
        }
        const top = res as Record<string, unknown>;
        if (top['success'] === false) {
            return null;
        }
        if ('data' in top && top['data'] == null) {
            return null;
        }
        let root: unknown =
            'data' in top && top['data'] != null ? top['data'] : res;
        if (Array.isArray(root) && root.length > 0) {
            root = root[0];
        }
        if (root == null || typeof root !== 'object') {
            return null;
        }
        const raw = root as Record<string, unknown>;
        let row: Record<string, unknown> | null = null;
        if (raw['vente_achat'] && typeof raw['vente_achat'] === 'object' && !Array.isArray(raw['vente_achat'])) {
            row = raw['vente_achat'] as Record<string, unknown>;
        } else if (Array.isArray(raw['vente_achat']) && (raw['vente_achat'] as unknown[]).length > 0) {
            const first = (raw['vente_achat'] as unknown[])[0];
            row = first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
        } else {
            row = raw;
        }
        if (!row || typeof row !== 'object') {
            return null;
        }
        return normalizeVenteAchatRow(row as Partial<VenteAchatParametreRow>);
    }
}
